import React, { useState } from 'react';
import { Plus, LayoutDashboard, LogOut } from 'lucide-react';
import { Link } from 'react-router-dom';
import { ProjectTable } from '../components/admin/ProjectTable';
import { ProjectForm } from '../components/admin/ProjectForm';
import { HeroImagesSection } from '../components/admin/HeroImagesSection';
import { Modal } from '../components/ui/Modal';
import { Button } from '../components/ui/Button';
import { useProjects, ProjectData } from '../hooks/useProjects';
import { useHeroImages } from '../hooks/useHeroImages';
export function AdminPage() {
  const {
    projects,
    addProject,
    updateProject,
    deleteProject,
    toggleFeatured
  } = useProjects();
  const {
    images: heroImages
  } = useHeroImages();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingProject, setEditingProject] = useState<ProjectData | null>(null);
  const handleCreate = () => {
    setEditingProject(null);
    setIsModalOpen(true);
  };
  const handleEdit = (project: ProjectData) => {
    setEditingProject(project);
    setIsModalOpen(true);
  };
  const handleDelete = (id: number) => {
    if (window.confirm('¿Estás seguro de que deseas eliminar este proyecto?')) {
      deleteProject(id);
    }
  };
  const handleSubmit = (data: ProjectData) => {
    if (editingProject && editingProject.id) {
      updateProject(editingProject.id, data);
    } else {
      addProject(data);
    }
    setIsModalOpen(false);
  };
  return <div className="min-h-screen bg-gray-50 font-sans text-gray-900" data-id="element-375">
      {/* Top Navigation */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-30" data-id="element-376">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-377">
          <div className="flex justify-between items-center h-16" data-id="element-378">
            <div className="flex items-center gap-3" data-id="element-379">
              <div className="bg-blue-600 p-2 rounded-lg" data-id="element-380">
                <LayoutDashboard className="h-5 w-5 text-white" data-id="element-381" />
              </div>
              <h1 className="text-xl font-bold text-gray-900" data-id="element-382">
                Panel de Administración
              </h1>
            </div>
            <div className="flex items-center gap-4" data-id="element-383">
              <Link to="/" className="text-sm font-medium text-gray-500 hover:text-gray-900 transition-colors" data-id="element-384">
                Ver Sitio Web
              </Link>
              <div className="h-4 w-px bg-gray-300" data-id="element-385" />
              <button className="flex items-center gap-2 text-sm font-medium text-red-600 hover:text-red-700 transition-colors" data-id="element-386">
                <LogOut className="h-4 w-4" data-id="element-387" />
                <span data-id="element-388">Salir</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-389">
        {/* Page Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8" data-id="element-390">
          <div data-id="element-391">
            <h2 className="text-2xl font-bold text-gray-900" data-id="element-392">Proyectos</h2>
            <p className="mt-1 text-gray-500" data-id="element-393">
              Gestiona el portafolio de proyectos y sus fotografías.
            </p>
          </div>
          <Button onClick={handleCreate} leftIcon={<Plus className="h-4 w-4" data-id="element-395" />} data-id="element-394">
            Nuevo Proyecto
          </Button>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-id="element-396">
          <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm" data-id="element-397">
            <p className="text-sm font-medium text-gray-500" data-id="element-398">Total Proyectos</p>
            <p className="text-3xl font-bold text-gray-900 mt-2" data-id="element-399">
              {projects.length}
            </p>
          </div>
          <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm" data-id="element-400">
            <p className="text-sm font-medium text-gray-500" data-id="element-401">
              Categorías Activas
            </p>
            <p className="text-3xl font-bold text-gray-900 mt-2" data-id="element-402">
              {new Set(projects.map(p => p.category)).size}
            </p>
          </div>
          <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm" data-id="element-403">
            <p className="text-sm font-medium text-gray-500" data-id="element-404">Destacados</p>
            <p className="text-3xl font-bold text-gray-900 mt-2" data-id="element-405">
              {projects.filter(p => p.featured).length}
              <span className="text-lg text-gray-400 font-normal" data-id="element-406">/3</span>
            </p>
          </div>
          <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm" data-id="element-407">
            <p className="text-sm font-medium text-gray-500" data-id="element-408">Imágenes Hero</p>
            <p className="text-3xl font-bold text-gray-900 mt-2" data-id="element-409">
              {heroImages.length}
              <span className="text-lg text-gray-400 font-normal" data-id="element-410">/4</span>
            </p>
          </div>
        </div>

        {/* Projects Management (NOW FIRST) */}
        <div className="mb-6" data-id="element-411">
          <h2 className="text-xl font-bold text-gray-900 mb-2" data-id="element-412">
            Listado de Proyectos
          </h2>
          <p className="text-gray-500 text-sm" data-id="element-413">
            Administra el contenido del portafolio. Destaca hasta 3 proyectos
            para el home.
          </p>
        </div>
        <ProjectTable projects={projects} onEdit={handleEdit} onDelete={handleDelete} onToggleFeatured={toggleFeatured} data-id="element-414" />

        <div className="my-12 border-t border-gray-200" data-id="element-415" />

        {/* Hero Images Management (NOW SECOND) */}
        <HeroImagesSection data-id="element-416" />
      </main>

      {/* Edit/Create Modal */}
      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title={editingProject ? 'Editar Proyecto' : 'Nuevo Proyecto'} data-id="element-417">
        <ProjectForm initialData={editingProject} onSubmit={handleSubmit} onCancel={() => setIsModalOpen(false)} data-id="element-418" />
      </Modal>
    </div>;
}